import {
  Box, Card, Dialog, DialogActions, DialogTitle, DialogContent,
  Button, Grid, styled, CardContent, Typography, Tooltip, Chip,
} from "@mui/material";
import CircularIndeterminate from "./circularProgress";
import { DataGridPro, GridToolbar } from "@mui/x-data-grid-pro";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useState, useEffect } from "react";
import { dataGridStyle } from "../theme";

const StyledCard = styled(Card)`
  flex: 1;
  width: auto;
  height: 78.4vh;
  background-color: #1E1E1E;
  background-image: none;
  border-radius: 15px;
  overflow: auto;
`;

// ── Grafico custom: una riga per IP con barra proporzionale + pulsante ──
// Exported so other views (e.g. the Asset Detail page's Network Issues tab) can reuse
// the same bar visual for a single IP without the surrounding DataGrid.
export const CustomBarList = ({ seriesData = [], categories = [], sourceIp, onBarClick }) => {
  if (!seriesData.length) return (
    <Typography sx={{ color: "#666", textAlign: "center", mt: 4 }}>No external data for this IP</Typography>
  );

  const maxCount = Math.max(...seriesData.map(d => d.count || 0), 1);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, px: 1, py: 1 }}>
      {seriesData.map((item, i) => {
        const ip    = categories[i] ?? item.ip ?? "";
        const count = item.count ?? 0;
        const pct   = Math.round((count / maxCount) * 100);

        return (
          <Box key={ip || i} sx={{
            display: "grid",
            gridTemplateColumns: "160px 1fr 60px auto",
            alignItems: "center",
            gap: 1,
            px: 1.5, py: 0.75,
            borderRadius: "6px",
            backgroundColor: "#1a1a1a",
            border: "1px solid #2a2a2a",
            "&:hover": { backgroundColor: "#222" },
          }}>
            {/* IP label */}
            <Typography sx={{ fontFamily: "monospace", fontSize: "0.8rem", color: "#ccc", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {ip}
            </Typography>

            {/* Barra proporzionale */}
            <Box sx={{ position: "relative", height: 14, borderRadius: 2, backgroundColor: "#2a2a2a", overflow: "hidden" }}>
              <Box sx={{
                position: "absolute", left: 0, top: 0, bottom: 0,
                width: `${pct}%`,
                borderRadius: 2,
                backgroundColor: "#4CCEAC",
                transition: "width 0.4s ease",
              }} />
            </Box>

            {/* Count */}
            <Typography sx={{ fontSize: "0.78rem", color: "#aaa", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
              {count.toLocaleString()}
            </Typography>

            {/* Pulsante View Threats — filtra per sourceIp (riga selezionata) E ip (barra) */}
            {onBarClick && (
              <Tooltip title={`Threats: source=${sourceIp}  dest=${ip}`} placement="left">
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<OpenInNewIcon sx={{ fontSize: 12 }} />}
                  onClick={() => onBarClick({ ipSource: sourceIp, ipDestination: ip })}
                  sx={{ fontSize: "0.68rem", height: 24, px: 1, whiteSpace: "nowrap" }}
                >
                  Threats
                </Button>
              </Tooltip>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

// ══════════════════════════════════════════════════════════════════
export const DataGridWithGraph = ({
  chargedGraph,
  tableData = [],
  charged,
  seriesData = [],
  selectedRow,
  categories = [],
  setSelectedRow,
  dataApi = '',
  onBarClick,   // callback({ sourceIp, destIp }) — entrambi i filtri
}) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [windowSize, setWindowSize] = useState({
    width:  window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    const handleResize = () =>
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const columns = [
    { field: "ip",    headerName: "Source Ip", flex: 1, align: "center", headerAlign: "center" },
    { field: "total", headerName: "Total",     flex: 1, align: "center", headerAlign: "center" },
    ...(dataApi !== 'broadcast' ? [{
      field: "select",
      headerName: "Select",
      headerAlign: "center",
      align: "center",
      flex: 1,
      renderCell: (cellValues) => (
        <Button
          sx={{
            fontWeight: 900,
            backgroundColor: "#2d2d2d !important",
            "&:hover": { backgroundColor: "#eaeaea !important", color: "#131C2D !important" },
          }}
          onClick={() => { setSelectedRow(cellValues.row.ip); setOpenDialog(true); }}
        >
          Select
        </Button>
      ),
    }] : []),
  ];

  // Header comune al pannello grafico
  const PanelHeader = ({ onClose }) => (
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 2, py: 1 }}>
      <Box display="flex" alignItems="center" gap={1}>
        <Typography variant="h6" fontWeight={700}>Chart for IP:</Typography>
        <Chip label={selectedRow} size="small" sx={{ fontFamily: "monospace", backgroundColor: "#459095", color: "#fff", fontWeight: 700 }} />
      </Box>
      <Box display="flex" alignItems="center" gap={1}>
        {onBarClick && (
          <Tooltip title={`Threats dove ${selectedRow} è src o dst`}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<OpenInNewIcon sx={{ fontSize: 13 }} />}
              onClick={() => onBarClick({ ipSource: selectedRow, ipDestination: selectedRow })}
              sx={{ fontSize: "0.72rem", height: 26 }}
            >
              View Threats
            </Button>
          </Tooltip>
        )}
        <button className="closeButton" onClick={onClose}>
          <CloseIcon />
        </button>
      </Box>
    </Box>
  );

  const chartContent = (
    chargedGraph
      ? <CustomBarList
          seriesData={seriesData}
          categories={categories}
          sourceIp={selectedRow}
          onBarClick={onBarClick}
        />
      : <Box sx={{ display: "flex", width: "100%", height: "100%", justifyContent: "center", pt: 6 }}>
          <CircularIndeterminate charged={chargedGraph} title="No data for this IP" />
        </Box>
  );

  return (
    <Box m="40px 0 0 0" height="80vh">
      <Grid container spacing={2}>

        {/* ── Tabella ─────────────────────────────────────────── */}
        <Grid height="80vh" size={selectedRow ? 6 : 12}>
          <Box height="100%">
            <DataGridPro
              sx={dataGridStyle}
              pagination
              localeText={{ noRowsLabel: "No problem detected" }}
              autoPageSize
              rows={tableData}
              loading={!charged}
              columns={columns}
              slots={{ toolbar: GridToolbar }}
              slotProps={{ toolbar: { printOptions: { disableToolbarButton: true } } }}
            />
          </Box>
        </Grid>

        {/* ── Pannello grafico ─────────────────────────────────── */}
        {selectedRow && (
          <Grid size={6}>
            {windowSize.width > 1024 ? (
              <StyledCard>
                <CardContent sx={{ p: 0 }}>
                  <PanelHeader onClose={() => setSelectedRow(null)} />
                  <Box sx={{ overflow: "auto", maxHeight: "calc(78.4vh - 60px)" }}>
                    {chartContent}
                  </Box>
                </CardContent>
              </StyledCard>
            ) : (
              <Dialog
                open={openDialog}
                onClose={() => setOpenDialog(false)}
                fullWidth maxWidth="xs"
                fullScreen={windowSize.width <= 1024}
              >
                <DialogTitle>
                  <PanelHeader onClose={() => { setOpenDialog(false); setSelectedRow(null); }} />
                </DialogTitle>
                <DialogContent sx={{ p: 0 }}>
                  <Card sx={{ background: "#2A354A", width: "100%", height: "73vh", overflow: "auto" }}>
                    {chartContent}
                  </Card>
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => { setOpenDialog(false); setSelectedRow(null); }} color="primary">
                    Close
                  </Button>
                </DialogActions>
              </Dialog>
            )}
          </Grid>
        )}
      </Grid>
    </Box>
  );
};